import {
  CalibrationType,
  Claim,
  Document,
  DocumentType,
  Glass,
  ImpactType,
  OperationType,
  SinisterGlass,
  XDamage,
  XImpactSize,
  XOperationType,
  XWindows,
} from '../';
import { SidexaGlass } from '../models';

export class SinisterDataUtils {
  public static getRequiredOperation(
    claim: Claim,
    operationTranslations
  ): string {
    if (claim?.operationType === 'Repair') {
      return operationTranslations.repairChoiceOperationWithGender;
    } else if (claim?.operationType === 'Replace') {
      return operationTranslations.replaceChoiceOperationWithGender;
    } else return '';
  }

  public static getRequiredOperationGendered(
    claim: Claim,
    operationTranslations
  ): string {
    if (claim?.operationType === 'Repair') {
      return operationTranslations.repairChoiceWithGenderOperationWithGender;
    } else if (claim?.operationType === 'Replace') {
      return operationTranslations.replaceChoiceWithGenderOperationWithGender;
    } else return '';
  }

  public static getRequiredOperationGenderedForCustomer(
    claim: Claim,
    operationTranslations
  ): string {
    if (claim?.operationType === 'Repair') {
      return (
        operationTranslations.repairChoiceWithCustomerGlassOperationWithGender ||
        operationTranslations.repairChoiceWithGenderOperationWithGender
      );
    } else if (claim?.operationType === 'Replace') {
      return (
        operationTranslations.replaceChoiceWithCustomerGlassOperationWithGender ||
        operationTranslations.replaceChoiceWithGenderOperationWithGender
      );
    } else return '';
  }

  public static getGlassName(glass: SinisterGlass, glassTranslation): string {
    if (
      glass?.glass === 'sideWindow' &&
      !!glass?.lateralPrecisions?.precision &&
      !!glass?.lateralPrecisions?.frontConductor
    ) {
      return glassTranslation[glass?.glass][
        glass?.lateralPrecisions?.precision +
          glass?.lateralPrecisions?.frontConductor
      ];
    } else if (
      glass?.glass === 'lights' &&
      !!glass?.light?.lightsEmplacement &&
      !!glass?.light?.lateral
    ) {
      return glassTranslation[glass?.glass][
        glass?.light?.lightsEmplacement + glass?.light?.lateral
      ];
    } else if (glass?.glass === 'mirror' && !!glass?.mirror) {
      return glassTranslation[glass?.glass][glass?.mirror];
    } else if (!!glass?.glass) {
      return glassTranslation[glass?.glass]['name'];
    } else return '';
  }

  public static getGlassNameGender(
    glass: SinisterGlass,
    glassGenderTranslation
  ): string {
    return SinisterDataUtils.getGlassName(glass, glassGenderTranslation);
  }

  public static isClaimPictureExists(
    documents: Document[],
    type: DocumentType
  ): boolean {
    if (!documents || documents.length === 0) {
      return false;
    }
    return (
      documents.filter(
        (picture: Document) => picture.type === type && picture.url
      ).length !== 0
    );
  }

  public static getNormalizedOperationType(
    operationType: OperationType
  ): XOperationType {
    return operationType === 'Repair' ? 'R' : 'NR';
  }

  public static getOperationTypeFromNormalized(
    normOpType: XOperationType
  ): OperationType {
    return normOpType === 'R' ? 'Repair' : 'Replace';
  }

  public static getNormalizedDamagedType(impactsType: ImpactType): XDamage {
    switch (impactsType) {
      case '1impact':
      case '2impactsLess':
      case '3impactsLess':
      case '2or3impacts':
        return 'I';
      case '3impactsMore':
      case 'more3impactsOrCrack':
      case 'crack':
        return 'C';
      default:
        return 'B';
    }
  }

  public static getNormalizedDamageSize(size: string): XImpactSize {
    return size === 'bigger2cm' ? 'B' : size === 'smaller2cm' ? 'S' : 'U';
  }

  public static getNormalizedCalibration(calibration: CalibrationType) {
    switch (calibration) {
      case 'true':
        return true;
      case 'false':
        return false;
      default:
        return null;
    }
  }

  /**
   * 'WS' : WindShield / Pare-brise @@@@@@@@@@@@
   * 'RW' : RearWindow / Lunette arrière
   * 'SWLDF' : SideWindowLeftDoorFront / Vitre portière avant gauche
   * 'SWLDR' : SideWindowLeftDoorRear / Vitre portière arrière gauche
   * 'SWLO' : SideWindowLeftOther / Autre vitrage latérale gauche
   * 'SWRDF' : SideWindowRightDoorFront / Vitre portière avant droite
   * 'SWRDR' : SideWindowRightDoorRear / Vitre portière arrière droite
   * 'SWRCR' : SideWindowRightCustodeRear / Custode Arrière droite
   * 'SWLCR' : SideWindowLeftCustodeRear / Custode Arrière gauche
   * 'SWRCF' : SideWindowRightCustodeFront / Custode Avant droite
   * 'SWLCF' : SideWindowLeftCustodeFront / Custode Avant gauche
   * 'SWRO' : SideWindowRightOther / Autre vitrage latérale droite
   * PS : SW inclut les custodes
   *    cas spéciaux :
   *        topWindow + lights (cas : deux avant ou arrière) ne sont pas gérés.
   * NOTE - Dans les prochaines API, Intégrer de nouveaux codes pour les déflecteurs arrières (ajoutés dans B2C FPB) qui sont des types de custodes
   */
  public static getNormalizedGlass(
    glass: SinisterGlass,
    custodeDiff: boolean
  ): XWindows {
    return glass?.glass === 'windshield'
      ? 'WS'
      : glass?.glass === 'rearWindow'
      ? 'RW'
      : glass?.glass === 'topWindow'
      ? 'RG'
      : glass?.glass === 'sideWindow' &&
        glass?.lateralPrecisions?.frontConductor === 'left' &&
        glass?.lateralPrecisions?.precision === 'conductorGlass'
      ? 'SWLDF'
      : glass?.glass === 'sideWindow' &&
        glass?.lateralPrecisions?.frontConductor === 'left' &&
        glass?.lateralPrecisions?.precision === 'anotherGlass'
      ? 'SWLDR'
      : glass?.glass === 'sideWindow' &&
        glass?.lateralPrecisions?.frontConductor === 'right' &&
        glass?.lateralPrecisions?.precision === 'conductorGlass'
      ? 'SWRDF'
      : glass?.glass === 'sideWindow' &&
        glass?.lateralPrecisions?.frontConductor === 'right' &&
        glass?.lateralPrecisions?.precision === 'anotherGlass'
      ? 'SWRDR'
      : glass?.glass === 'sideWindow' &&
        glass?.lateralPrecisions?.frontConductor === 'right' &&
        glass?.lateralPrecisions?.precision === 'custodeGlass'
      ? custodeDiff
        ? 'SWRCR'
        : 'SWRO'
      : glass?.glass === 'sideWindow' &&
        glass?.lateralPrecisions?.frontConductor === 'left' &&
        glass?.lateralPrecisions?.precision === 'custodeGlass'
      ? custodeDiff
        ? 'SWLCR'
        : 'SWLO'
      : glass?.glass === 'sideWindow' &&
        glass?.lateralPrecisions?.frontConductor === 'right' &&
        glass?.lateralPrecisions?.precision === 'custodeFrontGlass'
      ? custodeDiff
        ? 'SWRCF'
        : 'SWRO'
      : glass?.glass === 'sideWindow' &&
        glass?.lateralPrecisions?.frontConductor === 'left' &&
        glass?.lateralPrecisions?.precision === 'custodeFrontGlass'
      ? custodeDiff
        ? 'SWLCF'
        : 'SWLO'
      : glass?.glass === 'sideWindow' &&
        glass?.lateralPrecisions?.frontConductor === 'left' &&
        glass?.lateralPrecisions?.precision === 'anotherCustode'
      ? 'SWLO'
      : glass?.glass === 'sideWindow' &&
        glass?.lateralPrecisions?.frontConductor === 'right' &&
        glass?.lateralPrecisions?.precision === 'anotherCustode'
      ? 'SWRO'
      : glass?.glass === 'mirror' && glass?.mirror === 'left'
      ? 'ML'
      : glass?.glass === 'mirror' && glass?.mirror === 'right'
      ? 'MR'
      : glass?.glass === 'lights' &&
        glass?.light?.lateral === 'left' &&
        glass?.light?.lightsEmplacement === 'front'
      ? 'FLL'
      : glass?.glass === 'lights' &&
        glass?.light?.lateral === 'right' &&
        glass?.light?.lightsEmplacement === 'front'
      ? 'FLR'
      : glass?.glass === 'lights' &&
        glass?.light?.lateral === 'left' &&
        glass?.light?.lightsEmplacement === 'rear'
      ? 'BLL'
      : glass?.glass === 'lights' &&
        glass?.light?.lateral === 'right' &&
        glass?.light?.lightsEmplacement === 'rear'
      ? 'BLR'
      : '';
  }

  public static getGlassFromXWindow(window: XWindows): SinisterGlass {
    let sinisterGlass: SinisterGlass;
    switch (window) {
      case 'BLL':
        sinisterGlass = {
          glass: 'lights',
          light: { lateral: 'left', lightsEmplacement: 'rear' },
        };
        break;
      case 'BLR':
        sinisterGlass = {
          glass: 'lights',
          light: { lateral: 'right', lightsEmplacement: 'rear' },
        };
        break;
      case 'FLL':
        sinisterGlass = {
          glass: 'lights',
          light: { lateral: 'left', lightsEmplacement: 'front' },
        };
        break;
      case 'FLR':
        sinisterGlass = {
          glass: 'lights',
          light: { lateral: 'right', lightsEmplacement: 'front' },
        };
        break;
      case 'WS':
        sinisterGlass = {
          glass: 'windshield',
        };
        break;
      case 'RW':
        sinisterGlass = {
          glass: 'rearWindow',
        };
        break;
      case 'SWLDF':
        sinisterGlass = {
          glass: 'sideWindow',
          lateralPrecisions: {
            frontConductor: 'left',
            precision: 'conductorGlass',
          },
        };
        break;
      case 'SWLDR':
        sinisterGlass = {
          glass: 'sideWindow',
          lateralPrecisions: {
            frontConductor: 'left',
            precision: 'anotherGlass',
          },
        };
        break;
      case 'SWLCR':
        sinisterGlass = {
          glass: 'sideWindow',
          lateralPrecisions: {
            frontConductor: 'left',
            precision: 'anotherCustode',
          },
        };
        break;
      case 'SWLCF':
        sinisterGlass = {
          glass: 'sideWindow',
          lateralPrecisions: {
            frontConductor: 'left',
            precision: 'custodeFrontGlass',
          },
        };
        break;
      case 'SWRCR':
        sinisterGlass = {
          glass: 'sideWindow',
          lateralPrecisions: {
            frontConductor: 'right',
            precision: 'anotherCustode',
          },
        };
        break;
      case 'SWRCF':
        sinisterGlass = {
          glass: 'sideWindow',
          lateralPrecisions: {
            frontConductor: 'right',
            precision: 'custodeFrontGlass',
          },
        };
        break;

      case 'SWRDF':
        sinisterGlass = {
          glass: 'sideWindow',
          lateralPrecisions: {
            frontConductor: 'right',
            precision: 'conductorGlass',
          },
        };
        break;
      case 'SWRDR':
        sinisterGlass = {
          glass: 'sideWindow',
          lateralPrecisions: {
            frontConductor: 'right',
            precision: 'anotherGlass',
          },
        };
        break;
      case 'SWRO':
        sinisterGlass = {
          glass: 'sideWindow',
          lateralPrecisions: {
            frontConductor: 'left',
            precision: 'custodeGlass',
          },
        };
        break;
      case 'SWLO':
        sinisterGlass = {
          glass: 'sideWindow',
          lateralPrecisions: {
            frontConductor: 'left',
            precision: 'custodeGlass',
          },
        };
        break;
      case 'SWRO':
        sinisterGlass = {
          glass: 'sideWindow',
          lateralPrecisions: {
            frontConductor: 'left',
            precision: 'custodeGlass',
          },
        };
        break;
      case 'ML':
        sinisterGlass = {
          glass: 'mirror',
          mirror: 'left',
        };
        break;
      case 'MR':
        sinisterGlass = {
          glass: 'mirror',
          mirror: 'right',
        };
        break;
      case 'RG':
        sinisterGlass = {
          glass: 'topWindow',
        };
        break;
      default:
        null;
    }
    return sinisterGlass;
  }

  public static getSidexaCodeFromGlass(glass: Glass): SidexaGlass {
    switch (glass) {
      case 'lights':
        return '95-10-015';
      case 'rearWindow':
        return '64-35-010';
      case 'sideWindow':
        return '64-20-010';
      case 'topWindow':
        return '51-50-010';
      case 'windshield':
        return '64-10-010';
      default:
        null;
    }
  }

  public static getGlassNameDefaultTranslation(
    glass: Glass | SinisterGlass,
    globalGlossary: Object
  ): string {
    if (typeof glass === 'string') {
      return globalGlossary['sinisterGlass']['glass'][glass]['name'];
    }

    return globalGlossary['sinisterGlass']['glass'][
      glass?.glassExtended || glass?.glass
    ]['name'];
  }

  public static getOperationWithGlassTranslation(
    operationType: string,
    globalGlossary: Object
  ): string {
    let val = globalGlossary['operationType']['replaceChoiceOperation'];

    if (operationType === 'Repair') {
      val = globalGlossary['operationType']['repairChoiceOperation'];
    }

    return val;
  }
}
